import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Pause, Play, RotateCcw, SkipBack, SkipForward, Mic, MicOff, Download } from "lucide-react";
import { useProgress } from "@/modules/premiere-video/store";
import { BtnLink } from "@/components/kit";
import { useVoiceRecorder } from "@/lib/useVoiceRecorder";

const PARTS = [["hook", "Accroche"], ["demo", "Démonstration"], ["conclusion", "Conclusion"]];
const SPEEDS = [{ id: 2, label: "Posé" }, { id: 2.5, label: "Naturel" }, { id: 3, label: "Dynamique" }];

export const splitScript = (m4) =>
  PARTS.flatMap(([k, label]) =>
    (m4[k] || "").split(/\n+|(?<=[.!?…])\s+/).map((t) => t.trim()).filter(Boolean).map((text) => ({ text, label }))
  );

const words = (t) => t.split(/\s+/).filter(Boolean).length;
const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

function Ctrl({ onClick, label, testId, children, big }) {
  return (
    <button type="button" onClick={onClick} aria-label={label} data-testid={testId}
      className={`flex items-center justify-center rounded-full transition-[background-color,transform] active:scale-95 ${big ? "h-16 w-16 bg-gold text-ink hover:bg-[#d3b47e]" : "h-12 w-12 bg-white/10 text-white hover:bg-white/20"}`}>
      {children}
    </button>
  );
}

export default function Rehearse() {
  const { state } = useProgress();
  const lines = useMemo(() => splitScript(state.m4), [state.m4]);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [count, setCount] = useState(0);
  const [speed, setSpeed] = useState(2.5);
  const [elapsed, setElapsed] = useState(0);
  const [finished, setFinished] = useState(false);
  const toggleRef = useRef(null);
  const voice = useVoiceRecorder();

  const dur = (i) => Math.max(1.6, words(lines[i]?.text || "") / speed);
  const total = lines.reduce((a, _, i) => a + dur(i), 0);

  useEffect(() => {
    if (!count) return;
    const t = setTimeout(() => (count === 1 ? (setCount(0), setPlaying(true)) : setCount(count - 1)), 800);
    return () => clearTimeout(t);
  }, [count]);

  useEffect(() => {
    if (!playing) return;
    const tick = setInterval(() => setElapsed((e) => e + 0.1), 100);
    const next = setTimeout(() => {
      if (idx < lines.length - 1) setIdx(idx + 1);
      else { setPlaying(false); setFinished(true); }
    }, dur(idx) * 1000);
    return () => { clearInterval(tick); clearTimeout(next); };
  }, [playing, idx, speed, lines.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!voice.enabled) return;
    if (playing) voice.start();
    else if (finished) voice.stop();
    else voice.pause();
  }, [playing, finished, voice.enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const restart = () => { voice.discard(); setPlaying(false); setCount(0); setIdx(0); setElapsed(0); setFinished(false); };
  const toggle = () => {
    if (count) return setCount(0);
    if (playing) return setPlaying(false);
    if (finished) restart();
    if (elapsed === 0 || finished) setCount(3);
    else setPlaying(true);
  };
  toggleRef.current = toggle;

  useEffect(() => {
    const onKey = (e) => { if (e.code === "Space") { e.preventDefault(); toggleRef.current(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!lines.length)
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-5 bg-bx px-6 text-center text-white" data-testid="rehearse-empty">
        <p className="font-display text-2xl">Ton script est encore vide.</p>
        <p className="text-white/70">Écris ton accroche, ta démonstration et ta conclusion dans la mission 04.</p>
        <BtnLink to="/mission/4" variant="light" data-testid="rehearse-go-mission4">Écrire mon script</BtnLink>
      </div>
    );

  const cur = lines[idx];
  return (
    <div className="grain relative flex min-h-screen flex-col bg-bx text-white" data-testid="rehearse-page">
      <header className="relative z-10 flex items-center justify-between gap-3 px-5 py-4 sm:px-8">
        <Link to="/mission/4" className="flex items-center gap-2 text-sm text-white/80 hover:text-white" data-testid="rehearse-back"><ArrowLeft className="h-4 w-4" />Mon script</Link>
        <div className="flex items-center gap-4">
          {voice.recording && <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gold" data-testid="rehearse-recording-indicator"><span className="h-2 w-2 animate-pulse rounded-full bg-[#E5484D]" />Enregistrement</span>}
          <p className="font-display text-lg tabular-nums" data-testid="rehearse-timer">{fmt(elapsed)} <span className="text-white/50">/ ~{fmt(total)}</span></p>
        </div>
      </header>
      <div className="relative z-10 mx-5 h-1 overflow-hidden rounded-full bg-white/15 sm:mx-8">
        <div className="h-full bg-gold transition-[width] duration-500" style={{ width: `${((finished ? lines.length : idx) / lines.length) * 100}%` }} />
      </div>
      <main className="relative z-10 mx-auto flex w-full max-w-3xl flex-1 flex-col justify-center px-6 py-10 text-center">
        {count > 0 ? (
          <p className="font-display text-8xl text-gold" data-testid="rehearse-countdown">{count}</p>
        ) : finished ? (
          <div data-testid="rehearse-finished">
            <p className="font-display text-4xl">Bravo, script terminé !</p>
            <p className="mt-3 text-white/70">Durée : {fmt(elapsed)}. Recommence autant de fois que nécessaire pour gagner en aisance.</p>
            {voice.url && (
              <div className="mx-auto mt-8 max-w-md rounded-2xl bg-white/10 p-5 text-left" data-testid="rehearse-playback">
                <p className="text-sm font-semibold text-gold">Réécoute-toi</p>
                <p className="mt-1 text-sm text-white/70">Écoute ton rythme et ton ton : où ralentir, quelle phrase rendre plus naturelle ?</p>
                <audio controls src={voice.url} className="mt-4 w-full" data-testid="rehearse-audio" />
                <a href={voice.url} download={`ugc-go-repetition.${voice.ext}`} className="mt-3 inline-flex items-center gap-2 text-sm text-white/85 underline-offset-4 hover:underline" data-testid="rehearse-download">
                  <Download className="h-4 w-4" />Garder l'enregistrement sur mon appareil
                </a>
              </div>
            )}
          </div>
        ) : (
          <>
            <p className="min-h-[1.5rem] text-base text-white/35 sm:text-lg">{lines[idx - 1]?.text}</p>
            <p className="mt-6 text-xs font-semibold uppercase tracking-[0.25em] text-gold" data-testid="rehearse-part">{cur.label} · {idx + 1}/{lines.length}</p>
            <p key={idx} className="mt-4 animate-fade-up font-display text-3xl leading-snug sm:text-5xl" data-testid="rehearse-current-line">{cur.text}</p>
            <p className="mt-8 min-h-[1.5rem] text-base text-white/45 sm:text-lg">{lines[idx + 1]?.text}</p>
          </>
        )}
      </main>
      <footer className="relative z-10 space-y-5 px-5 pb-8 sm:px-8">
        <div className="flex items-center justify-center gap-4">
          <Ctrl onClick={restart} label="Recommencer" testId="rehearse-restart"><RotateCcw className="h-5 w-5" /></Ctrl>
          <Ctrl onClick={() => { setFinished(false); setIdx(Math.max(0, idx - 1)); }} label="Phrase précédente" testId="rehearse-prev"><SkipBack className="h-5 w-5" /></Ctrl>
          <Ctrl big onClick={toggle} label={playing ? "Pause" : "Lecture"} testId="rehearse-play-toggle">{playing || count ? <Pause className="h-6 w-6" /> : <Play className="ml-1 h-6 w-6" />}</Ctrl>
          <Ctrl onClick={() => { setFinished(false); setIdx(Math.min(lines.length - 1, idx + 1)); }} label="Phrase suivante" testId="rehearse-next"><SkipForward className="h-5 w-5" /></Ctrl>
        </div>
        <div className="mx-auto flex w-fit gap-1 rounded-full bg-white/10 p-1" role="radiogroup" aria-label="Rythme">
          {SPEEDS.map((s) => (
            <button key={s.id} type="button" role="radio" aria-checked={speed === s.id} onClick={() => setSpeed(s.id)} data-testid={`rehearse-speed-${s.label.toLowerCase()}`}
              className={`rounded-full px-4 py-2 text-sm transition-colors ${speed === s.id ? "bg-white text-bx" : "text-white/80 hover:text-white"}`}>{s.label}</button>
          ))}
        </div>
        <div className="flex flex-col items-center gap-2">
          <button type="button" onClick={voice.toggle} aria-pressed={voice.enabled} data-testid="rehearse-mic-toggle"
            className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors ${voice.enabled ? "bg-gold text-ink" : "bg-white/10 text-white/85 hover:bg-white/20"}`}>
            {voice.enabled ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
            {voice.enabled ? "Ma voix sera enregistrée" : "M'enregistrer pendant la lecture"}
          </button>
          {voice.error ? <p className="text-center text-xs text-[#F3C1C4]" data-testid="rehearse-mic-error">{voice.error}</p>
            : <p className="text-center text-xs text-white/50">L'enregistrement reste sur ton appareil, il n'est envoyé nulle part.</p>}
        </div>
        <p className="hidden text-center text-xs text-white/50 sm:block">Barre d'espace : lecture / pause</p>
      </footer>
    </div>
  );
}
